import {useState, useEffect, useRef, useCallback} from 'react';

/**
 * @description custom hook to persist a value in localStorage
 * @param {string} key a key to store the value in localStorage
 * @param {T} initialValue the initial value to use if no value is found in localStorage
 * @return {[T, Function, Function]} the tuple of length 3, a stateful value, a function to update it and options object.
 */
const useLocalStorage = <T>(
  key: string,
  initialValue: T,
): [
  T,
  (value: T | ((val: T) => T)) => void,
  {
    signalClearKeys: (keys: string[]) => void;
    getImperativeValue: (key: string) => T | null;
  },
] => {
  const [storedValue, setStoredValue] = useState<T>(() => initialValue);
  /** Signal flag to know when to clear before unmount */
  const clearSignal = useRef<{keys: string[]}>({keys: []});

  /**
   * @description sets the value in localStorage,
   * in order to prevent any uncontrolled updates,
   * ensure that `setValue` is NOT called unconditionally,
   * instead it should be called based on specific conditions or user interactions.
   * @example
   * const MyComponent = () => {
   *   const [value, setValue] = useLocalStorage('myKey', 'initialValue');
   *   const handleClick = () => {
   *     setValue('newValue');
   *   };
   *
   *  return (
   *    <div>
   *      <div>{value}</div>
   *      <button onClick={handleClick}>Update Value</button>
   *    </div>
   *  );
   * };
   * @param {T | Function} value to store in localStorage
   * @return {undefined} a procedure/effect function
   */
  const setValue = (value: T | ((val: T) => T)) => {
    setStoredValue((currentStoredValue) => {
      try {
        const valueToStore = value instanceof Function ? value(currentStoredValue) : value;
        if (valueToStore === null || typeof valueToStore === 'undefined') {
          window.localStorage.removeItem(key);
        } else {
          window.localStorage.setItem(key, JSON.stringify(valueToStore));
        }

        return valueToStore;
      } catch (error) {
        console.error('Error writing to localStorage:', error);

        return currentStoredValue;
      }
    });
  };

  /**
   * @description gets the value from localStorage imperatively
   * - needed on mount to read local storage
   * - needed for some edge cases where we need to read local storage immediately, outside of local state lifecycle
   * @param {string} key the key to get the value from localStorage
   * @return {T | null} the value from localStorage or null if not found
   */
  const getImperativeValue = useCallback(
    (key: string): T | null => {
      try {
        const item = window.localStorage.getItem(key);
        return item ? JSON.parse(item) : null;
      } catch (error) {
        console.error('Error reading from localStorage:', error);
        return null;
      }
    },
    [key],
  );

  /**
   * @description signal function to clear localStorage keys
   * - needed to clear specific keys on unmount or immediately post unmount
   * when for example this hook is within a component that is unmounted
   * @param {string[]} keys the keys to clear from localStorage on unmount
   */
  const signalClearKeys = useCallback((keys: string[]) => {
    clearSignal.current.keys = keys;
  }, []);

  /**
   * @description effect to clear localStorage keys on unmount
   * if `signalClearKeys` was called
   */
  useEffect(() => {
    return () => {
      if (clearSignal.current.keys.length) {
        clearSignal.current.keys.forEach((keyToRemove) => {
          window.localStorage.removeItem(keyToRemove);
        });
        clearSignal.current.keys = [];
      }
    };
  }, []);

  /**
   * @description syncs the state with localStorage
   */
  useEffect(() => {
    const value = getImperativeValue(key);
    setStoredValue(value ?? initialValue);
  }, [key, getImperativeValue]);

  return [storedValue, setValue, {signalClearKeys, getImperativeValue}];
};

export default useLocalStorage;
